/**
 * Date helpers. The API speaks UTC ISO-8601; the clinic lives in Europe/Kyiv,
 * so everything shown to staff is rendered in Kyiv time regardless of the browser zone.
 */
import { getLang } from '@/i18n'

export const KYIV = 'Europe/Kyiv'

function locale() {
  return getLang() === 'en' ? 'en-GB' : 'uk-UA'
}

type Input = string | number | Date | null | undefined

function toDate(v: Input): Date | null {
  if (v == null || v === '') return null
  const d = v instanceof Date ? v : new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

export function fmtDateTime(v: Input): string {
  const d = toDate(v)
  if (!d) return '—'
  return new Intl.DateTimeFormat(locale(), {
    timeZone: KYIV,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

export function fmtDate(v: Input, opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }): string {
  const d = toDate(v)
  if (!d) return '—'
  return new Intl.DateTimeFormat(locale(), { timeZone: KYIV, ...opts }).format(d)
}

export function fmtTime(v: Input): string {
  const d = toDate(v)
  if (!d) return '—'
  return new Intl.DateTimeFormat(locale(), { timeZone: KYIV, hour: '2-digit', minute: '2-digit' }).format(d)
}

export function fmtDayMonth(v: Input): string {
  return fmtDate(v, { day: 'numeric', month: 'short' })
}

/** "5 хв тому", "через 2 год" … */
export function fmtRelative(v: Input, now: Date = new Date()): string {
  const d = toDate(v)
  if (!d) return '—'
  const diff = (d.getTime() - now.getTime()) / 1000
  const abs = Math.abs(diff)
  const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto' })
  if (abs < 60) return rtf.format(Math.round(diff), 'second')
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour')
  if (abs < 86400 * 30) return rtf.format(Math.round(diff / 86400), 'day')
  return fmtDate(d)
}

export interface KyivParts {
  year: number
  month: number // 1-12
  day: number
  hour: number
  minute: number
  weekday: number // 1 = Monday … 7 = Sunday
}

const partsFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: KYIV,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  weekday: 'short',
  hourCycle: 'h23',
})

const WEEKDAYS: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 }

export function kyivParts(v: Date | string | number): KyivParts {
  const d = v instanceof Date ? v : new Date(v)
  const p: Record<string, string> = {}
  for (const x of partsFmt.formatToParts(d)) p[x.type] = x.value
  return {
    year: Number(p.year),
    month: Number(p.month),
    day: Number(p.day),
    hour: Number(p.hour) % 24,
    minute: Number(p.minute),
    weekday: WEEKDAYS[p.weekday] ?? 1,
  }
}

const pad = (n: number) => String(n).padStart(2, '0')

/** Calendar day in Kyiv as YYYY-MM-DD. */
export function kyivDayKey(v: Date | string | number): string {
  const p = kyivParts(v)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}

/** Offset of Kyiv from UTC in minutes at a given instant. */
export function kyivOffsetMinutes(at: Date): number {
  const p = kyivParts(at)
  const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute)
  return Math.round((asUTC - Math.floor(at.getTime() / 60000) * 60000) / 60000)
}

/** Convert a Kyiv wall-clock time to an instant (DST-correct). */
export function fromKyiv(year: number, month: number, day: number, hour = 0, minute = 0): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute)
  let off = kyivOffsetMinutes(new Date(guess))
  let ts = guess - off * 60000
  const off2 = kyivOffsetMinutes(new Date(ts))
  if (off2 !== off) {
    off = off2
    ts = guess - off * 60000
  }
  return new Date(ts)
}

/** Value for <input type="datetime-local"> in Kyiv time. */
export function toKyivInput(v: Input): string {
  const d = toDate(v)
  if (!d) return ''
  const p = kyivParts(d)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`
}

/** Parse <input type="datetime-local"> (Kyiv wall clock) into an ISO UTC string. */
export function fromKyivInput(s: string): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(s)
  if (!m) return undefined
  return fromKyiv(+m[1], +m[2], +m[3], m[4] ? +m[4] : 0, m[5] ? +m[5] : 0).toISOString()
}

/** Start of the Kyiv day containing `v`, as an instant. */
export function kyivStartOfDay(v: Date | string | number): Date {
  const p = kyivParts(v)
  return fromKyiv(p.year, p.month, p.day)
}

export function addDays(dayKey: string, n: number): string {
  const [y, m, d] = dayKey.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + n))
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`
}

export function dayKeyToInstant(dayKey: string, hour = 0, minute = 0): Date {
  const [y, m, d] = dayKey.split('-').map(Number)
  return fromKyiv(y, m, d, hour, minute)
}

/** Monday-first weeks covering a month: array of weeks, each 7 day keys. */
export function monthGrid(year: number, month: number): string[][] {
  const first = new Date(Date.UTC(year, month - 1, 1))
  const wd = (first.getUTCDay() + 6) % 7 // 0 = Monday
  const start = addDays(`${year}-${pad(month)}-01`, -wd)
  const weeks: string[][] = []
  let cur = start
  for (let w = 0; w < 6; w++) {
    const week: string[] = []
    for (let i = 0; i < 7; i++) {
      week.push(cur)
      cur = addDays(cur, 1)
    }
    weeks.push(week)
    const [ny, nm] = cur.split('-').map(Number)
    if (ny * 12 + nm > year * 12 + month) break
  }
  return weeks
}

export function weekDays(dayKey: string): string[] {
  const [y, m, d] = dayKey.split('-').map(Number)
  const wd = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7
  const monday = addDays(dayKey, -wd)
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i))
}

export function todayKey(): string {
  return kyivDayKey(new Date())
}

export function monthName(year: number, month: number): string {
  return new Intl.DateTimeFormat(locale(), { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, 15)),
  )
}

export function weekdayShort(i: number): string {
  // i: 0 = Monday
  return new Intl.DateTimeFormat(locale(), { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, 1 + i)))
}
